import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, BarChart3, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryModal } from "../components/EnquiryModal";
import { useTitle } from "../hooks/useTitle";

export default function Courses() {
  useTitle("Courses");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="courses-page">
      <section className="bg-[#0A192F] relative overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 hero-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow light>All Courses</Eyebrow>
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl leading-[1.1]">
            Practical programs in Tally, GST, TDS & accounting.
          </h1>
          <p className="mt-5 text-slate-300 text-base max-w-xl leading-relaxed">
            Every course is hands-on, assignment-driven and built around real business workflows.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <p className="text-sm text-[#627D98]" data-testid="courses-loading">Loading courses...</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.06}>
                <div
                  data-testid={`courses-card-${c.slug}`}
                  className="group h-full flex flex-col bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-[0_20px_50px_rgba(10,25,47,0.12)] hover:-translate-y-1.5 transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={c.image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    {c.badge && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#0A192F]/90 text-[#C59B27] text-[10px] font-mono-sn font-bold tracking-wider">
                        {c.badge}
                      </span>
                    )}
                    {c.featured && (
                      <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#C59B27] text-[#0A192F] text-[10px] font-mono-sn font-bold tracking-wider">
                        FEATURED
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h2 className="font-display font-bold text-lg text-[#0A192F] leading-snug">{c.title}</h2>
                    <p className="text-sm text-[#486581] leading-relaxed mt-2 flex-1">{c.short_desc}</p>
                    <div className="flex flex-wrap gap-3 mt-4 text-xs font-semibold text-[#334E68]">
                      <span className="flex items-center gap-1"><Clock size={12} className="text-[#0F5132]" /> {c.duration}</span>
                      <span className="flex items-center gap-1"><BarChart3 size={12} className="text-[#0F5132]" /> {c.level}</span>
                    </div>
                    <div className="flex gap-2 mt-5">
                      <Link
                        to={`/courses/${c.slug}`}
                        data-testid={`courses-view-${c.slug}`}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#0A192F] text-white text-xs font-bold hover:bg-[#0F5132] transition-colors"
                      >
                        View Course <ArrowRight size={13} />
                      </Link>
                      <EnquiryModal
                        defaultInterest={c.title}
                        source={`courses-page-${c.slug}`}
                        trigger={
                          <button data-testid={`courses-enquire-${c.slug}`} className="px-4 py-2.5 rounded-lg border border-[#0A192F] text-[#0A192F] text-xs font-bold hover:bg-[#0A192F] hover:text-white transition-colors">
                            Enquire
                          </button>
                        }
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
